import { createFileRoute, Link } from '@tanstack/react-router';
import {
    moderatorLeaderboardStatsQuery,
    submissionDailyStatsQuery,
} from '~/util/queries/staff/stats';
import { DailyStatsChart } from './-DailyStatsChart';
import { TotalStatsPieChart } from './-TotalStatsPieChart';
import { moderatorStatsParamsSchema } from './-SearchParams';
import { userQuery } from '~/util/queries/public/users';
import { ScrollArea } from '~/primitives/scroll-area';
import { ListTabSwitch } from '~/partials/TabSwitch';
import { RoleTags } from '~/partials/Tags';
import { ExternalLink } from 'lucide-react';
import { hasScope } from '~/auth/utils';
import { shiftsQuery } from '~/util/queries/staff/shifts';
import { useScopes } from '~/components/Providers/AuthProvider';
import { UserShiftsTable } from './-UserShiftsTable';
import { UserProfilePicture } from '~/components/UserProfilePicture';

export const Route = createFileRoute('/_authenticated/staff/stats/$userid')({
    validateSearch: moderatorStatsParamsSchema,
    component: RouteComponent,
    loaderDeps: ({ search: { list, shifts, page, per_page } }) => ({
        list,
        shifts,
        page,
        per_page,
    }),
    loader: async ({
        deps: { list, page, per_page, shifts: shiftsQueryParams },
        context: { queryClient, auth },
        params: { userid },
    }) => {
        const [data, total, user, shifts] = await Promise.all([
            queryClient.fetchQuery(
                submissionDailyStatsQuery({
                    list,
                    reviewer: userid,
                    page,
                    per_page,
                }),
            ),
            queryClient.fetchQuery(
                moderatorLeaderboardStatsQuery({
                    list,
                    reviewer_id: userid,
                    include_hidden_reviewers: true,
                }),
            ),
            queryClient.fetchQuery(userQuery({ userid })),
            hasScope(auth, 'shift_manage')
                ? queryClient.fetchQuery(
                      shiftsQuery({
                          user: { id: userid, username: '', global_name: '' },
                          ...shiftsQueryParams,
                      }),
                  )
                : Promise.resolve(null),
        ]);

        return { data, total, user, shifts };
    },
});

function RouteComponent() {
    const { user, shifts } = Route.useLoaderData({
        select: ({ user, shifts }) => ({ user, shifts }),
    });

    const { hasScope } = useScopes();

    return (
        <main className="h-full flex justify-center">
            <div className="flex flex-col items-center gap-4 w-full h-full bg-background-dark rounded-2xl py-4">
                <ScrollArea className="w-full p-4 lg:h-[calc(100vh-150px)]">
                    <div className="flex gap-2">
                        <h1 className="text-secondary-foreground">
                            User statistics
                        </h1>
                        <ListTabSwitch from="/staff/stats/$userid" />
                    </div>
                    <div className="border rounded-xl p-4 px-8 gap-4 flex  my-2">
                        <UserProfilePicture
                            user={user}
                            className="size-24 sm:size-32"
                            smallBadge
                        />
                        <div>
                            <h2>
                                {user.global_name}{' '}
                                <Link
                                    to="/profile/user/$userid"
                                    params={{ userid: user.username }}
                                >
                                    <ExternalLink className="inline size-8 hover:text-secondary-light cursor-pointer transition-colors" />
                                </Link>
                            </h2>
                            <span>{user.username}</span>
                            <p>Role(s)</p>
                            <RoleTags items={user.roles} />
                        </div>
                    </div>

                    <div className="flex w-full gap-2 my-2">
                        <DailyStatsChart />
                        <TotalStatsPieChart />
                    </div>
                    {shifts && hasScope('shift_manage') && (
                        <div className="border rounded-xl p-4 my-2">
                            <h2 className="mb-4">
                                Previous Shifts
                                <Link
                                    to="/staff/shifts"
                                    search={{
                                        user: user,
                                        tab: 'all',
                                    }}
                                >
                                    <ExternalLink className="inline size-8 hover:text-secondary-light cursor-pointer transition-colors" />
                                </Link>
                            </h2>
                            <UserShiftsTable />
                        </div>
                    )}
                </ScrollArea>
            </div>
        </main>
    );
}
